/**
 * /go-to-market-strategy-template 的中文版本。结构、className、调性与
 * GoToMarketStrategyTemplate.tsx 保持一致;差异化角度同样是:GTM 模板在 PPT 或
 * Notion 里只是一张填空表,而筹备上线恰恰是其中决策变动最频繁的阶段——定价反复
 * 改、某个渠道被砍、上线日期顺延。静态文档跟不上,于是定位、定价、上线计划在发布
 * 前就彼此脱节。这里先把真正的模板(七个板块,逐条写清)交给读者,再说明
 * Draftlize 如何把每个 GTM 决策做成结构化卡片,改一处上游、下游自动标 stale。
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function GoToMarketStrategyTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/go-to-market-strategy-template"
      documentTitle="不会漂移的 Go-to-Market 策略模板 · Draftlize"
      documentDescription="一份可直接抄走的 go to market strategy template(GTM 模板):目标市场与 ICP、定位与信息、定价与套餐、渠道、上线时间线、成功指标、关键风险。问题在于:PPT 或文档里的 GTM 模板,一进入筹备期就开始漂移。在 Draftlize,每个 GTM 决策都是一张结构化卡片;改一处上游决策,定价、渠道、时间线会自动标 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · Go-to-Market</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份 GTM 策略模板,<br/>
            <em>让上线前的决策不再漂移。</em>
          </h1>
          <p className="lede">
            你是来找一份 <em>go to market strategy template</em>(GTM 策略模板)的——要板块、要标题、要一份能直接丢进 PPT 或文档、上线前用得上的东西。它就在下面,拿去用。但你得清楚自己拿到的是什么:GTM 模板本质是一张填空表,而筹备上线,恰恰是其中决策变动最频繁的阶段。定价反复改、某个渠道被砍、日期顺延一周——而一页幻灯片或一篇 Notion,根本不知道它记下的定位、套餐、上线计划已经对不上了。Draftlize 给你同样的七个板块,但把每个决策做成一张 AI agent 能读能写的卡片:只要有一个决策一动,所有下游都会自己标成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板本体</span>
            <h2 className="serif">七个板块,<em>抄到哪儿都能用。</em></h2>
          </div>
          <p>这就是完整的 go-to-market 计划模板。它在 PPT、wiki 或文档里都成立——这正是大多数 GTM 计划最终会收敛成的形状。可以原样拿去用。下一节讲的是:当每个决策不再是一条要点、而变成一张卡片时,会发生什么。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '目标市场与 ICP',
              hint: '卖给谁',
              body: '你要切入的市场,以及其中的理想客户画像——行业、规模、岗位、你具体替他解掉的那个痛点。计划里其余一切都是它的下游;这一步错了,信息、定价、渠道就全瞄准了错的人。',
            },
            {
              k: '定位与信息',
              hint: '怎么讲',
              body: '一句话主张、你竞争的品类、你要取代的替代方案,以及证据。"为有 Y 困扰的 X,我们是那个能做到 W 的 Z。"一旦 ICP 变了,这是第一个必须跟着动的板块。',
            },
            {
              k: '定价与套餐',
              hint: '怎么收费',
              body: '收费模型(按席位、按量、分档、免费试用)、各档,以及每档放什么。这是上线筹备里最易变的一个决策,也是被其他板块默默依赖最多的一个——从信息口径,到你成功指标里的营收模型。',
            },
            {
              k: '渠道',
              hint: '怎么被找到',
              body: '触达 ICP 的路径——内容与 SEO、付费、外呼、合作、社区、产品驱动。哪个主打、哪个测试、哪个先放着。渠道是建立在某个 ICP 和某个价位之上的;两者任一变了,这套渠道组合就不再是对的那套。',
            },
            {
              k: '上线时间线',
              hint: '什么时候',
              body: '顺序与日期——内测、公测、正式发布、上线那一刻本身,以及卡住每一步的前置工作。时间线是一张伪装成清单的依赖网;一个日期顺延,所有挂在它上面的都应跟着移,而在幻灯片里,什么都不会动。',
            },
            {
              k: '成功指标',
              hint: '怎么算成了',
              body: '用数字定义"成了"——激活、转化、CAC、回本周期、你据以掌舵的北极星。它们是对着你的定价和 ICP 定的,所以上游一变,你在这里设的目标就被悄悄作废了。',
            },
            {
              k: '关键风险',
              hint: '什么会搞砸它',
              body: '那些一旦判断错就会让整个计划沉没的假设——那个可能转化不了的渠道、那个市场可能不买账的价格、那个可能顺延的依赖。把它们列出来很便宜;真正的价值,是在别处某个决策把一个风险变成事实的那一刻,你能察觉。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          模板就是这些。下面讲:为什么筹备上线正是它开始漂移的时刻——以及我们对此做了什么。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">GTM 模板为什么会 <em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策变动最快的时刻,恰好就在上线前</h3>
            <p>GTM 计划最有用的时候,是它稳定的时候——而筹备上线,正是它最不稳定的时候。定价改两遍,某个渠道在一次糟糕的测试后被砍,公测日期顺延。一份 PPT 只抓住了移动靶的某一帧,然后在余下的整个筹备期里默默说着假话。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">板块之间相互依赖,文档却不知道</h3>
            <p>改了定价模型,你的信息、渠道组合、成功指标就全过期了——因为每一项都建立在旧价格之上。PPT 里没有任何东西把它们连起来,于是它们一次一处地越漂越远,直到整个计划开始自相矛盾。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">三份文档,三个版本的事实</h3>
            <p>定价在一张表里,定位在一份 PPT 里,时间线在项目工具里。到了上线那周,每一份说的都略有出入,没人分得清哪份是最新的——于是团队对着一份没有任何单一文档真正写明的计划上线。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板,<em>变成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个板块成了一张卡片,而不是一页幻灯片</h3>
            <p>在 Draftlize,模板不是一份空白 PPT——每个 GTM 决策都是一张有类型的卡片:目标市场与 ICP、定位、定价与套餐、渠道、上线时间线、成功指标、关键风险。结构化、可按 ID 寻址,能从 spec 或对话里被引用,而不是埋在第 14 页幻灯片里。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一处定价,下游自动标 stale</h3>
            <p>因为决策之间的关联是真实的链接、而非隐含的默契,改动定价决策,会让依赖它的信息、渠道组合、指标自动标成 stale——就像构建系统让一个被改文件的所有下游全部失效。计划不可能在不告诉你的情况下漂移到自相矛盾。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能读它、也能写它</h3>
            <p>Claude Code 或 Cursor,通过 MCP,在起草上线简报、定价页或发布稿之前,先读完每一条相关的 GTM 决策——再把新决策按同样的形状写回去。计划不再是一份没人重新打开的 PPT,而成了每一轮都会被读到的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          GTM 模板告诉你该定哪些决策。它没法告诉你:其中某个决策什么时候悄悄把计划的其余部分弄坏了。
        </blockquote>
        <cite>保留这七个板块,让底座替你让它们彼此对齐。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是 go-to-market（GTM）策略？', a: 'GTM 策略是你把产品推向市场、触达客户的计划：目标市场与 ICP、定位、定价与打包、渠道、上线时间线、成功指标、关键风险。它把你造出来的东西，连到谁会买、以及他们会怎么听说它。' },
            { q: 'GTM 策略应该包含什么？', a: '上面这七部分：目标市场与 ICP、定位、定价与打包、渠道、上线时间线、成功指标、关键风险。它们彼此咬合——定价塑造渠道，定位塑造 messaging——所以一处一改，若没有东西把它们连起来，就会悄悄让其余部分失效。' },
            { q: 'GTM 策略和营销计划有什么区别？', a: 'GTM 策略是更大的那个押注：你服务谁、如何定位、如何定价、用哪些渠道触达。营销计划是它底下的一个执行层——交付 messaging 的那些 campaign 和内容。GTM 策略定这盘棋，营销计划下其中一部分。' },
            { q: 'Draftlize 怎么让 GTM 计划保持同步？', a: '每个 GTM 决策都是一张卡片，彼此之间的链接是真的。改动定价决策，凡是建立在它之上的 messaging、渠道组合、指标都自动标成 stale——于是计划不会在你不知情时，悄悄和它自己失去一致。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">开始你的 GTM 计划,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>按量付费,用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面的模板拿去用,或者开一个项目,让 agent 从你现有的上线文档里把它填好——然后替你把每个 GTM 决策、以及一切依赖它的东西,一路对齐到上线及以后。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
      <TemplateCta template="gtm" locale="zh" />
    </MarketingShell>
  );
}
